import React, {useState} from "react";
import { Card, Tabs } from "antd";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import httpClient from "../../../api/module/httpClient";
import ProgramSchedule from "./ProgramSchedule";
import Orientation from "./Orientation";
import Announcements from "./Announcements";
import Faculty from "./Faculty";
import FacultyCurrentDashboardItem from "./FacultyDiscussionBoard";
import CardUser from "../../CertificateDashboard/CardUser";
import FacultyDiploma from "../FacultyDiploma";
import DiscussionBoard from "../../CertificateDashboard/DiscussionBoard";
import moment from "moment/moment";
import { convertToLocalTime } from "../../../utils/format";
import { INTERNAL_LINKS } from "../../../enum";

export default function FacultyDashboardItem({ userProfile }) {
  const [currentDiscussionBoardKey, ___] = useState("Preparation Week and Orientation Session");
  const params = useParams();

  const slug = params.slug;

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ["CURRENT_FACULTY_DASHBOARD", slug],
    queryFn: async () => {
      const { data } = await httpClient.get(`private/certificate-programs/${slug}/faculty/current-dashboard`);

      return data;
    },
    enabled: !!slug,
  });


  const myClass = data?.CourseClasses.find(item => {
    return (item.CertificateClassInstructors ?? []).some(instructor => instructor.UserId === userProfile?.id) || item.instructorIds.includes(userProfile?.id);
  });

  const myClasses = data?.CourseClasses.filter(item => {
    return (item.CertificateClassInstructors ?? []).some(instructor => instructor.UserId === userProfile?.id) || item.instructorIds.includes(userProfile?.id);
  });

  const isFacilitator = (data?.facilitatorIds.includes(userProfile?.id));

  const preparationWeekClass = (data?.CourseClasses || []).filter((courseClass) => {return courseClass.classType === "preparation"});

  const instructors = (data?.CertificateClassInstructors || []).reduce((acc, current) => {
    const x = acc.find(item => item.UserId === current.UserId);
    if (!x) {
      return acc.concat([current]);
    } else {
      return acc;
    }
  }, [])

  const TabData = [
    (isFacilitator ? null : {
      title: "Your Classes",
      content: () => <div style={{ whiteSpace: "pre-wrap" }}>
        <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
          {[...(data?.CertificateClassInstructors || [])]
            // First filter for the user's ID
            .filter(item => item.UserId === userProfile?.id)
            // Then remove duplicates based on UserId
            .filter((item, index, self) =>
              index === self.findIndex(t => t.UserId === item.UserId)
            )
            .map((item, index) => (
              <CardUser
                boxShadow={false}
                instructor={{
                  ...item,
                  classes: myClasses,
                }}
                key={item.id || index} // Better to use item.id if available instead of index
              />
            ))
          }
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {(myClasses ?? []).map(item => {

            let startDate = convertToLocalTime(item?.startDate, item?.timezone);
            let startTime = convertToLocalTime(item?.startTime, item?.timezone);
            let endTime = convertToLocalTime(item?.endTime, item?.timezone);

            return (
              <div>
                <h2>{item.title}</h2>
                <div dangerouslySetInnerHTML={{ __html: item.description || item.descriptionForCertificate}} />

                <div>
                  <p
                    style={{
                      margin: "0px",
                      marginTop: "1rem",
                      marginBottom: "5px",
                    }}
                  >
                    <b>Class Date</b>:
                    {` ${(startDate || moment())?.format("LL")}`}
                  </p>
                  <p
                    style={{
                      margin: "0px",
                      marginTop: "5px",
                      marginBottom: "5px",
                    }}
                  >
                    <b>Class Start Time:</b>{" "}
                    {(startTime || moment())?.format("HH:mm")}{" "}
                    {moment.tz.guess()}
                  </p>
                  <p
                    style={{
                      margin: "0px",
                      marginTop: "5px",
                      marginBottom: "5px",
                    }}
                  >
                    <b>Class End Time:</b>{" "}
                    {(endTime || moment())?.format("HH:mm")}{" "}
                    {moment.tz.guess()}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>,
    }),
    {
      title: "Faculty Toolkit",
      content: () => <Orientation program={data} />,
    },
    {
      title: "Announcements",
      content: () => <Announcements certificateId={data?.id} />,
    },
    {
      title: "Program Schedule",
      content: () => <ProgramSchedule program={data} />,
    },
    {
      title: "Other Faculty",
      content: () => <Faculty classes={data?.CourseClasses} />,
    },
    {
      title: "Faculty Discussion Board",
      content: () => <FacultyCurrentDashboardItem CourseClasses={data?.CourseClasses} userProfile={userProfile} />,
    },
    {
      title: "Student Discussion Board",
      content: () => (
        <div style={{
          padding: "1rem 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
          height: "100%",
        }}>
          <div style={{ width: "100%", marginTop: "1rem" }}>
            {preparationWeekClass.map((item) => {

              return (
                <>
                  {String(item.title).startsWith(currentDiscussionBoardKey) && (
                    <div
                      style={{
                        border: ".5px solid black",
                        borderRadius: "16px",
                        padding: "1rem",
                        width: "100%",
                      }}
                    >
                      {item.classType === "groupDiscussion" ? (
                        <DiscussionBoard
                          slug={slug}
                          userProfile={userProfile}
                          certificateUser={item?.User}
                          certificateId={data?.id}
                          isGroupDiscussionBoard={true}
                          hasNotification={false}
                        />
                      ) : (
                        <DiscussionBoard
                          slug={slug}
                          userProfile={userProfile}
                          certificateUser={item?.User}
                          certificateClassId={item.id}
                          hasNotification={false}
                        />
                      )}
                    </div>
                  )}
                </>
              );
            })}
          </div>
        </div>
      ),
    },
    (isFacilitator ? null : {
      title: "Faculty Diploma",
      content: () => <FacultyDiploma userProfile={userProfile} myClasses={myClasses} />,
    }),
    {
      title: <Link to={`${INTERNAL_LINKS.CERTIFICATE_PROGRAMS_FACULTY_DASHBOARD_PAST}`}>Back to dashboard</Link>,
      content: () => <></>,
    }
  ].filter(Boolean);

  return (
    <div style={{ boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)", padding: "1rem 0" }}>
      <Card style={{ borderRadius: "20px" }}>
        <Tabs
          // activeKey={currentTab}
          // activeKey="5"
          tabPosition="left"
          className="certificate-program-tabs"
          // onChange={(e) => setCurrentTab(e)}
          destroyInactiveTabPane={true}
          items={TabData.map((item, index) => {
            return {
              label: typeof item.title === "function" ? item.title() : item.title,
              key: index.toString(),
              children: item.content(),
            };
          })}
        />
      </Card>
    </div>
  );
}